import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-horizontal:flex-col",
        className
      )}
      {...props}
    />
  );
}

/**
 * `segmented` (28px, Cursor's "All / Upgrades 1"), `pane` (26px, Cursor's
 * "Changes / Desktop / Terminal") and `pill` (28px rounded-full, Cursor's
 * "Team / Mine / Explore" on a page). Selected tabs use a fill, never an
 * underline. `default` and `line` are deprecated aliases. The in-IDE tab
 * strips do not use this component.
 */
const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center text-fg-3 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col group-data-vertical/tabs:items-stretch",
  {
    variants: {
      variant: {
        segmented: "h-7 gap-0.5 rounded-md bg-fill-1 p-0.5",
        pane: "h-6.5 gap-1",
        pill: "h-7 gap-1",
        default: "h-7 gap-0.5 rounded-md bg-fill-1 p-0.5",
        line: "h-6.5 gap-1"
      }
    },
    defaultVariants: {
      variant: "segmented"
    }
  }
);

type TabsListVariant = NonNullable<
  VariantProps<typeof tabsListVariants>["variant"]
>;

const canonicalVariant = (v: TabsListVariant): "segmented" | "pane" | "pill" =>
  v === "pill" ? "pill" : v === "pane" || v === "line" ? "pane" : "segmented";

function TabsList({
  className,
  variant = "segmented",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={canonicalVariant(variant ?? "segmented")}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-full items-center justify-center gap-1.5 rounded-md px-2 text-base whitespace-nowrap text-fg-3 transition-colors duration-100 outline-none group-data-vertical/tabs:h-7 group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50 data-active:text-fg [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        "group-data-[variant=segmented]/tabs-list:flex-1 group-data-[variant=segmented]/tabs-list:rounded-sm group-data-[variant=segmented]/tabs-list:data-active:bg-elevated group-data-[variant=segmented]/tabs-list:data-active:ring-1 group-data-[variant=segmented]/tabs-list:data-active:ring-line-2",
        "group-data-[variant=pane]/tabs-list:hover:bg-fill-1 group-data-[variant=pane]/tabs-list:data-active:bg-fill-2",
        "group-data-[variant=pill]/tabs-list:rounded-full group-data-[variant=pill]/tabs-list:px-3 group-data-[variant=pill]/tabs-list:text-fg-2 group-data-[variant=pill]/tabs-list:hover:bg-fill-1 group-data-[variant=pill]/tabs-list:data-active:bg-fill-2 group-data-[variant=pill]/tabs-list:data-active:text-fg",
        className
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 text-base outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
